import React, { useRef, useState, useEffect } from 'react'
import { motion, useMotionValue, useTransform, useVelocity, useViewportScroll, useSpring } from 'framer-motion'
import { gsap } from 'gsap/all'
import { createShowFromBottomAnimation } from 'src/helpers/animations'
import Ampersand from 'src/images/svg/ampersand.svg'
import styled from 'styled-components'

const StyledAmpersand = styled.img`
  width: auto;
  display: inline-block;
  height: 3em;
  transform: translateY(0.3em);

  @media (min-width: 768px) {
    height: 4em;
    transform: translateY(0.5em);
  }
`

interface IScrollingMarqueeProps {
  text: string
  className?: string
  baseVelocity?: number
}

// Funkcja pomocnicza do bezpiecznego zapętlania wartości
const wrap = (min: number, max: number, v: number) => {
  const rangeSize = max - min
  return ((((v - min) % rangeSize) + rangeSize) % rangeSize) + min
}

const ScrollingMarquee: React.FC<IScrollingMarqueeProps> = ({ text, className = '', baseVelocity = -0.8 }) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const marqueeWrapperRef = useRef<HTMLDivElement>(null)
  const timeline = useRef<any>(null)
  const [contentWidth, setContentWidth] = useState(0)

  useEffect(() => {
    if (!timeline.current) {
      timeline.current = gsap.timeline()
    }
    if (marqueeWrapperRef.current) {
      createShowFromBottomAnimation(timeline.current, marqueeWrapperRef.current)
    }
  }, [])

  // Mierzymy dokładną szerokość jednego bloku co do piksela,
  // by uniknąć problemu judderowania / migania przeglądarki na setnych częściach procenta
  useEffect(() => {
    const measure = () => {
      if (contentRef.current) {
        setContentWidth(contentRef.current.getBoundingClientRect().width)
      }
    }
    measure()
    // Uaktualniamy pomiar, jeśli użytkownik zmieni rozmiar okna
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  const baseX = useMotionValue(0)
  const { scrollY } = useViewportScroll()
  const scrollVelocity = useVelocity(scrollY)

  // Wygładzamy skoki prędkości ze scrolla
  const smoothVelocity = useSpring(scrollVelocity, {
    damping: 50,
    stiffness: 400
  })

  // Tworzymy transformację X operującą na pikselach
  const x = useTransform(baseX, (v) => {
    if (contentWidth === 0) return "0px"
    return `${wrap(-contentWidth, 0, v)}px`
  })

  // Własna pętla animacji (zastępstwo dla useAnimationFrame z nowszych wersji Framer Motion)
  useEffect(() => {
    let animationFrameId: number
    let lastTime = performance.now()

    const loop = (time: number) => {
      const delta = time - lastTime
      lastTime = time

      // Odczytujemy wygładzoną prędkość przewijania i skalujemy w dół (dzielnik 250 dla mniejszej czułości)
      let currentVelocity = smoothVelocity.get() / 250

      // speed = -2 (w lewo) odejmujemy np. 10 (scroll w dół), by dostać -12 (szybciej w lewo)
      // jeśli scroll w górę (-10), to -2 - (-10) daje +8 (cofa się w prawo!)
      let speed = baseVelocity - currentVelocity

      // Normalizujemy po czasie trwania pojedynczej klatki (ok ~16.66ms)
      baseX.set(baseX.get() + speed * (delta / 16.666))

      animationFrameId = requestAnimationFrame(loop)
    }

    animationFrameId = requestAnimationFrame(loop)

    return () => cancelAnimationFrame(animationFrameId)
  }, [baseVelocity, smoothVelocity, baseX])

  const renderItems = () => (
    <div className="flex items-center flex-shrink-0">
      {[...Array(6)].map((_, i) => (
        <React.Fragment key={i}>
          <span className="font-palanquinDark text-6xl md:text-7xl lg:text-7xl xl:text-8xl font-bold uppercase px-8 text-black dark:text-white leading-none">
            {text}
          </span>
          <div className="flex-shrink-0 px-4">
            <StyledAmpersand
              src={Ampersand}
              alt="&"
            />
          </div>
        </React.Fragment>
      ))}
    </div>
  )

  return (
    <div ref={marqueeWrapperRef} className={`w-full overflow-hidden py-20 md:py-24 xl:py-32 bg-transparent border-y border-secondaryGray500 dark:border-white/10 select-none opacity-0 ${className}`}>
      <motion.div
        className="flex flex-nowrap will-change-transform w-max"
        style={{ x }}
      >
        <div ref={contentRef} className="flex flex-shrink-0">
          {renderItems()}
        </div>
        <div className="flex flex-shrink-0">
          {renderItems()}
        </div>
      </motion.div>
    </div>
  )
}

export default ScrollingMarquee
